import { useMemo } from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { CatResultsResponse } from "../../../client/types.gen";
import { formatExactMoneyDollars } from "../Money";
import { SelectedModel, RETURN_PERIODS, getPerilDisplayName } from "./types";
import styles from "./CatCard.module.css";

interface EpCurvesTabProps {
  selectedModel: SelectedModel;
  rmsResultsData: CatResultsResponse | null;
  spatialKatResultsData: CatResultsResponse | null;
}

interface EpDisplayRow {
  peril: string;
  values: Record<number, number>;
}

interface EpTableData {
  perspective: string;
  metric_type: string;
  rows: EpDisplayRow[];
}

const PERSPECTIVE_ORDER: Record<string, number> = { GU: 0, GR: 1, RL: 2 };
const METRIC_ORDER: Record<string, number> = { OEP: 0, AEP: 1 };
const MONEY_FMT = { withRounding: true, withDollarSign: true } as const;

export const EpCurvesTab: React.FC<EpCurvesTabProps> = ({
  selectedModel,
  rmsResultsData,
  spatialKatResultsData,
}) => {
  const { tables, totalAal } = useMemo(() => {
    const aggregate =
      selectedModel === "spatialkat"
        ? spatialKatResultsData?.spatialkat?.aggregate
        : rmsResultsData?.rms?.aggregate;
    const epCurves = aggregate?.ep_curves;
    const totalAal = aggregate?.aal_combined_groundup ?? null;
    if (!epCurves) return { tables: [], totalAal };

    const grouped = new Map<string, EpTableData>();
    for (const curve of epCurves) {
      if (curve.perspective === "GR") continue;
      const key = `${curve.perspective}-${curve.metric_type}`;
      let table = grouped.get(key);
      if (!table) {
        table = {
          perspective: curve.perspective,
          metric_type: curve.metric_type,
          rows: [],
        };
        grouped.set(key, table);
      }
      const rpValues: Record<number, number> = {};
      for (const rp of RETURN_PERIODS) {
        rpValues[rp] =
          curve.return_period_values?.[String(rp)] ??
          curve.return_period_values?.[`${rp}.0`] ??
          0;
      }
      table.rows.push({
        peril: getPerilDisplayName(curve.peril),
        values: rpValues,
      });
    }

    const tables = Array.from(grouped.values());
    tables.sort((a, b) => {
      const pDiff =
        (PERSPECTIVE_ORDER[a.perspective] ?? 99) -
        (PERSPECTIVE_ORDER[b.perspective] ?? 99);
      if (pDiff !== 0) return pDiff;
      return (
        (METRIC_ORDER[a.metric_type] ?? 99) -
        (METRIC_ORDER[b.metric_type] ?? 99)
      );
    });
    return { tables, totalAal };
  }, [selectedModel, rmsResultsData, spatialKatResultsData]);

  if (tables.length === 0) {
    return (
      <Message
        severity="info"
        text="No EP curve data available for the selected model. Run an analysis to generate results."
        className={styles.messageMargin}
      />
    );
  }

  return (
    <div className={styles.epCurvesContainer}>
      <div className={styles.epGrid}>
        {tables.map((table) => (
          <div
            key={`${table.perspective}-${table.metric_type}`}
            className={styles.section}
          >
            <h4 className={styles.epSectionTitle}>
              {table.perspective} {table.metric_type}
            </h4>
            <DataTable value={table.rows} size="small">
              <Column field="peril" header="Peril" />
              {RETURN_PERIODS.map((rp) => (
                <Column
                  key={rp}
                  header={`1/${rp}`}
                  body={(row: EpDisplayRow) =>
                    row.values[rp]
                      ? formatExactMoneyDollars(row.values[rp], MONEY_FMT)
                      : ""
                  }
                  align="right"
                />
              ))}
            </DataTable>
          </div>
        ))}
      </div>
      {totalAal !== null && (
        <div>
          <strong>Total AAL (GU):</strong>{" "}
          {formatExactMoneyDollars(totalAal, MONEY_FMT)}
        </div>
      )}
    </div>
  );
};
